// <jc-select>: wraps a native <select> and replaces the OS picker with a button and a list under it. The select stays in the DOM
// as the source of truth (code reads and writes its value and listens for "change"); without JS it works as it always did.
// Programmatic value or text changes do not fire events: call sync() after them to repaint the button.
// Lists of more than 12 options get a search field (unless data-no-search) (accent-insensitive); it takes focus only with a fine pointer, so a phone
// keeps its keyboard down until the field is tapped. Pattern: button with aria-haspopup="listbox" and a listbox of options;
// the focus stays on the button (or the search field) and the active option is named by aria-activedescendant.
const SEARCH_FROM = 12;

const plain = text => text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

let uid = 0;

class JcSelect extends HTMLElement {
  connectedCallback() {
    // connectedCallback runs again if the element is moved in the DOM: bind once
    if (this.ready) return;
    this.select = this.querySelector("select");
    if (!this.select) return;
    this.ready = true;
    const id = `jc-select-${++uid}`;
    const name = this.select.getAttribute("aria-label") || this.select.labels?.[0]?.querySelector("span")?.textContent.trim() || "";
    this.name = name;

    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.className = "jc-select-btn";
    this.button.setAttribute("role", "combobox");
    this.button.setAttribute("aria-haspopup", "listbox");
    this.button.setAttribute("aria-expanded", "false");
    this.button.setAttribute("aria-controls", `${id}-list`);
    this.label = document.createElement("span");
    this.label.className = "jc-select-text";
    const ico = document.createElement("span");
    ico.className = "acc-ico";
    ico.setAttribute("aria-hidden", "true");
    this.button.append(this.label, ico);

    this.panel = document.createElement("div");
    this.panel.className = "jc-select-panel";
    this.panel.hidden = true;
    this.list = document.createElement("ul");
    this.list.className = "jc-select-list";
    this.list.id = `${id}-list`;
    this.list.setAttribute("role", "listbox");
    if (name) this.list.setAttribute("aria-label", name);
    this.panel.append(this.list);
    this.prepend(this.button);
    this.append(this.panel);

    this.select.tabIndex = -1;
    this.select.setAttribute("aria-hidden", "true");
    this.setAttribute("ready", "");

    this.button.addEventListener("click", () => (this.isOpen ? this.close() : this.open()));
    // Firefox fires a click on the keyup of Space even when keydown handled it: that would toggle the list twice
    this.button.addEventListener("keyup", e => { if (e.key === " ") e.preventDefault(); });
    // a click on the <label> focuses the select: hand it to the button
    this.select.addEventListener("focus", () => this.button.focus());
    this.select.addEventListener("change", () => this.sync());
    this.addEventListener("keydown", e => this.onKey(e));
    // the panel sits inside the label in the drawer: a click there must not reach the label (it would click the control again)
    this.panel.addEventListener("click", e => e.preventDefault());
    // mousedown on the panel keeps the focus where it is (button or search field)
    this.panel.addEventListener("mousedown", e => { if (e.target !== this.search) e.preventDefault(); });
    this.list.addEventListener("click", e => {
      const option = e.target.closest("[role=option]");
      if (option) this.pick(option);
    });
    this.list.addEventListener("pointermove", e => {
      // a finger dragging to scroll the list must not leave a grey row behind: only a mouse moves the highlight
      if (e.pointerType !== "mouse") return;
      const option = e.target.closest("[role=option]");
      if (option && option !== this.current) this.activate(option);
    });
    this.onOutside = e => { if (!this.contains(e.target)) this.close(); };
    this.sync();
  }

  // the search field is decided on the first open, when the options are final; data-no-search opts out (the footer's currencies)
  ensureSearch() {
    if (this.searched) return;
    this.searched = true;
    if (this.hasAttribute("data-no-search") || this.select.options.length <= SEARCH_FROM) return;
    this.search = document.createElement("input");
    this.search.type = "search";
    this.search.className = "jc-select-search";
    this.search.placeholder = this.dataset.search || "";
    this.search.setAttribute("aria-label", this.dataset.search || this.name);
    this.search.setAttribute("aria-controls", this.list.id);
    this.search.autocomplete = "off";
    this.search.addEventListener("input", () => this.filter());
    this.panel.prepend(this.search);
  }

  get isOpen() { return !this.panel.hidden; }

  // repaint the button from the select (the list is built on open, so it is always current)
  sync() {
    if (!this.ready) return;
    const text = this.select.selectedOptions[0]?.textContent.trim() || "";
    this.label.textContent = text;
    this.button.setAttribute("aria-label", this.name ? `${this.name}: ${text}` : text);
  }

  open() {
    if (this.isOpen) return;
    this.sync();
    this.ensureSearch();
    this.list.replaceChildren(...[...this.select.options].map((o, i) => {
      const li = document.createElement("li");
      li.setAttribute("role", "option");
      li.id = `${this.list.id}-${i}`;
      li.dataset.value = o.value;
      li.dataset.key = plain(o.textContent);
      li.textContent = o.textContent.trim();
      li.setAttribute("aria-selected", o.selected ? "true" : "false");
      return li;
    }));
    if (this.search) this.search.value = "";
    this.panel.hidden = false;
    this.setAttribute("open", "");
    this.button.setAttribute("aria-expanded", "true");
    // no room below (the drawer's footer sits at the bottom of the screen): open upwards; the panel never outgrows its side
    this.classList.remove("up");
    this.panel.style.maxHeight = "";
    const rect = this.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    const up = below < this.panel.offsetHeight && rect.top > below;
    this.classList.toggle("up", up);
    // the stylesheet's max-height is the ceiling (the footer's is shorter than the drawer's); the space on its side can only lower it
    const ceiling = parseFloat(getComputedStyle(this.panel).maxHeight) || 260;
    let max = Math.max(120, Math.min(ceiling, (up ? rect.top : below) - 8));
    // a whole number of rows (also at the ceiling), so the last visible one is never sliced in half (chrome = the search field and the borders)
    const row = this.list.firstElementChild?.offsetHeight || 0;
    const chrome = this.panel.offsetHeight - this.list.offsetHeight;
    if (row > 0) max = Math.max(row, Math.floor((max - chrome) / row) * row) + chrome;
    this.panel.style.maxHeight = `${max}px`;
    this.activate(this.list.querySelector("[aria-selected=true]") || this.list.firstElementChild, true);
    if (this.search && matchMedia("(pointer: fine)").matches) this.search.focus({ preventScroll: true });
    document.addEventListener("pointerdown", this.onOutside, true);
  }

  close(refocus = true) {
    if (!this.isOpen) return;
    this.panel.hidden = true;
    this.removeAttribute("open");
    this.button.setAttribute("aria-expanded", "false");
    this.button.removeAttribute("aria-activedescendant");
    this.search?.removeAttribute("aria-activedescendant");
    document.removeEventListener("pointerdown", this.onOutside, true);
    if (refocus) this.button.focus();
  }

  disconnectedCallback() {
    document.removeEventListener("pointerdown", this.onOutside, true);
  }

  get visible() { return [...this.list.children].filter(li => !li.hidden); }

  get focused() { return this.search && document.activeElement === this.search ? this.search : this.button; }

  activate(option, scroll = false) {
    this.current?.classList.remove("act");
    this.current = option;
    if (!option) { this.focused.removeAttribute("aria-activedescendant"); return; }
    option.classList.add("act");
    this.button.setAttribute("aria-activedescendant", option.id);
    this.search?.setAttribute("aria-activedescendant", option.id);
    if (scroll) option.scrollIntoView({ block: "nearest" });
  }

  move(to) {
    const items = this.visible;
    if (!items.length) return;
    const i = items.indexOf(this.current);
    const next = to === "first" ? 0 : to === "last" ? items.length - 1 : Math.min(items.length - 1, Math.max(0, i + to));
    this.activate(items[next], true);
  }

  filter() {
    const q = plain(this.search.value.trim());
    for (const li of this.list.children) li.hidden = !li.dataset.key.includes(q);
    this.activate(this.visible[0] || null);
    this.list.scrollTop = 0;
  }

  pick(option) {
    this.select.value = option.dataset.value;
    this.sync();
    this.close();
    this.select.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // typing a letter on the button jumps to the next option that starts with it (or with the typed prefix)
  typeahead(e) {
    clearTimeout(this.typed);
    this.typedText = (this.typedText || "") + plain(e.key);
    this.typed = setTimeout(() => { this.typedText = ""; }, 700);
    const items = this.visible;
    const from = items.indexOf(this.current) + (this.typedText.length === 1 ? 1 : 0);
    const ordered = [...items.slice(from), ...items.slice(0, from)];
    const hit = ordered.find(li => li.dataset.key.startsWith(this.typedText));
    if (hit) this.activate(hit, true);
  }

  onKey(e) {
    const typing = e.target === this.search;
    if (e.key === "Tab") {
      if (!this.isOpen) return;
      // from the search field the focus goes to the button first; Shift+Tab stops there, Tab moves on to the next control
      const fromSearch = this.focused === this.search;
      this.close(fromSearch);
      if (fromSearch && e.shiftKey) e.preventDefault();
      return;
    }
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    if (!this.isOpen) {
      if (e.target !== this.button) return;
      if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key) || (e.key.length === 1 && !e.altKey)) {
        e.preventDefault();
        this.open();
        if (e.key.length === 1 && e.key !== " ") this.typeahead(e);
      }
      return;
    }
    switch (e.key) {
      case "Escape": e.preventDefault(); e.stopPropagation(); this.close(); break;
      case "ArrowDown": e.preventDefault(); this.move(1); break;
      case "ArrowUp": e.preventDefault(); this.move(-1); break;
      case "Home": if (!typing) { e.preventDefault(); this.move("first"); } break;
      case "End": if (!typing) { e.preventDefault(); this.move("last"); } break;
      case "Enter": e.preventDefault(); if (this.current) this.pick(this.current); break;
      case " ":
        if (typing && this.search.value) break;
        e.preventDefault();
        if (this.current) this.pick(this.current);
        break;
      default:
        if (!typing && e.key.length === 1) { e.preventDefault(); this.typeahead(e); }
    }
  }
}

if (!customElements.get("jc-select")) customElements.define("jc-select", JcSelect);
